import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2, Upload } from "lucide-react";
import { ATT_BLUE, ATT_REP_ROLES, ATT_SHIRT_SIZES, ATT_LOGOS } from "@/attData";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full rounded-xl border border-black/15 bg-white px-4 py-3 font-body text-sm text-[#1d2329] placeholder:text-[#9aa4b0] focus:border-[#00A8E0] focus:outline-none transition-colors duration-300";

const Label = ({ children }) => (
  <p className="font-body text-xs tracking-[0.2em] uppercase text-[#5a6572] mb-2">{children}</p>
);

const Pills = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        data-testid={`${testid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => onChange(opt)}
        className={`rounded-full px-5 py-2 font-body text-sm font-semibold border transition-colors duration-300 ${value === opt ? "border-[#00A8E0] bg-[#00A8E0]/10 text-[#0077a8]" : "border-black/15 text-[#5a6572] hover:border-[#00A8E0]/50"}`}
      >
        {opt}
      </button>
    ))}
  </div>
);

export default function ATTRepApplyPage() {
  const [params] = useSearchParams();
  const roleParam = ATT_REP_ROLES.find((r) => r.id === params.get("role"));
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", address: "", city: "", zip_code: "",
    gender: "", shirt_size: "", position: roleParam ? roleParam.name : "",
    background_check: "", drug_test: "", convicted: "", convicted_explain: "",
    signature: "", signature_date: new Date().toISOString().slice(0, 10),
  });
  const [badge, setBadge] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone number.");
      return;
    }
    if (!form.position) { toast.error("Please choose the position you're applying for."); return; }
    if (!form.background_check || !form.drug_test || !form.convicted) {
      toast.error("Please answer the background, drug test, and conviction questions.");
      return;
    }
    if (!form.signature) { toast.error("Please sign the application by typing your full name."); return; }
    setSending(true);
    try {
      let badge_path = null, badge_filename = null;
      if (badge) {
        const fd = new FormData();
        fd.append("file", badge);
        const up = await fetch(`${API}/api/att/rep-upload-badge`, { method: "POST", body: fd });
        if (!up.ok) {
          const err = await up.json().catch(() => ({}));
          throw new Error(err.detail || "Badge photo upload failed.");
        }
        const data = await up.json();
        badge_path = data.badge_path;
        badge_filename = data.badge_filename;
      }
      const res = await fetch(`${API}/api/att/rep-apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, badge_path, badge_filename }),
      });
      if (!res.ok) throw new Error("Submission failed.");
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#f4f6f8] min-h-screen" data-testid="att-rep-apply-page">
      <div className="h-24 bg-[#06111f]" />
      <div className="bg-white shadow-md">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-center gap-5 flex-wrap">
          <span className="inline-flex items-center rounded-xl bg-[#06111f] px-4 py-2">
            <img src="/logo-full.png" alt="Heritage Family Protection Agency" className="h-9 w-auto object-contain" data-testid="rep-header-heritage-logo" />
          </span>
          <img src={ATT_LOGOS.att} alt="AT&T" className="h-9 w-auto object-contain" />
          <p className="font-body text-[10px] font-bold tracking-[0.3em] uppercase text-[#1d2329]">Authorized Dealer · Sales Careers</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-14 md:py-20">
        {done ? (
          <div data-testid="att-rep-success" className="rounded-3xl border border-[#00A8E0]/40 bg-white p-10 md:p-14 text-center shadow-sm">
            <CheckCircle2 size={48} className="mx-auto" style={{ color: ATT_BLUE }} strokeWidth={1.2} />
            <h1 className="mt-6 font-serif-display text-3xl md:text-4xl text-[#1d2329]">Application received.</h1>
            <p className="mt-4 font-body font-light text-base md:text-lg text-[#3c4650] max-w-xl mx-auto">
              Thank you for applying to join our AT&amp;T sales team! We'll review your application and reach out shortly.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <span className="h-[3px] w-10" style={{ backgroundColor: ATT_BLUE }} />
              <p className="font-body text-xs md:text-sm font-bold tracking-[0.25em] uppercase" style={{ color: ATT_BLUE }}>AT&amp;T Sales Rep Application</p>
            </div>
            <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1d2329]">
              Join the team.
            </h1>
            <p className="mt-4 font-body font-light text-base md:text-lg text-[#3c4650]">
              Door to door, events, national retail, business sales, or work from home — fill this out and we'll get you started.
            </p>

            <form onSubmit={submit} className="mt-10 rounded-3xl border border-black/10 bg-white p-8 md:p-10 space-y-7 shadow-sm" data-testid="att-rep-form">
              <div>
                <Label>Position you're applying for *</Label>
                <Pills options={ATT_REP_ROLES.map((r) => r.name)} value={form.position} onChange={setVal("position")} testid="rep-position" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="rep-first-name" className={inputCls} placeholder="First name *" value={form.first_name} onChange={set("first_name")} />
                <input data-testid="rep-last-name" className={inputCls} placeholder="Last name *" value={form.last_name} onChange={set("last_name")} />
              </div>
              <input data-testid="rep-address" className={inputCls} placeholder="Street address" value={form.address} onChange={set("address")} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="rep-city" className={inputCls} placeholder="City" value={form.city} onChange={set("city")} />
                <input data-testid="rep-zip" className={inputCls} placeholder="ZIP code" value={form.zip_code} onChange={set("zip_code")} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="rep-phone" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
                <input data-testid="rep-email" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
              </div>

              <div>
                <Label>Gender</Label>
                <Pills options={["Male", "Female"]} value={form.gender} onChange={setVal("gender")} testid="rep-gender" />
              </div>

              <div>
                <Label>Shirt size</Label>
                <Pills options={ATT_SHIRT_SIZES} value={form.shirt_size} onChange={setVal("shirt_size")} testid="rep-shirt" />
              </div>

              <div>
                <Label>Upload badge picture</Label>
                <label
                  data-testid="rep-badge-upload"
                  className="flex items-center justify-center gap-3 rounded-xl border border-dashed border-black/25 bg-[#f7f9fa] px-4 py-6 cursor-pointer hover:border-[#00A8E0] transition-colors"
                >
                  <Upload size={18} style={{ color: ATT_BLUE }} />
                  <span className="font-body text-sm text-[#3c4650]">
                    {badge ? badge.name : "Click to upload a clear headshot (JPG or PNG, max 8 MB)"}
                  </span>
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="hidden" onChange={(e) => setBadge(e.target.files[0] || null)} />
                </label>
              </div>

              <div>
                <Label>Can you pass a background check? *</Label>
                <Pills options={["Yes", "No"]} value={form.background_check} onChange={setVal("background_check")} testid="rep-background" />
              </div>
              <div>
                <Label>Can you pass a drug test? *</Label>
                <Pills options={["Yes", "No"]} value={form.drug_test} onChange={setVal("drug_test")} testid="rep-drugtest" />
              </div>
              <div>
                <Label>Have you ever been convicted of a crime? *</Label>
                <Pills options={["Yes", "No"]} value={form.convicted} onChange={setVal("convicted")} testid="rep-convicted" />
                {form.convicted === "Yes" && (
                  <textarea data-testid="rep-convicted-explain" rows={2} className={`${inputCls} mt-3`} placeholder="Please briefly explain..." value={form.convicted_explain} onChange={set("convicted_explain")} />
                )}
              </div>

              <div className="border-t border-black/10 pt-6">
                <Label>Sign &amp; date</Label>
                <p className="font-body text-xs text-[#8a94a0] mb-3">
                  By typing your full name below, you certify that the information provided is true and complete.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="rep-signature" className={`${inputCls} italic`} placeholder="Type your full name to sign *" value={form.signature} onChange={set("signature")} />
                  <input data-testid="rep-date" type="date" className={inputCls} value={form.signature_date} onChange={set("signature_date")} />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="rep-submit"
                className="btn-sweep w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-body text-sm font-semibold tracking-wide text-white disabled:opacity-60"
                style={{ backgroundColor: ATT_BLUE }}
              >
                {sending ? "Submitting..." : "Submit My Application"} <Send size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
